import { DateTime, Interval } from 'luxon';
import type { RawCapacity } from 'types/summaries';
import debug from 'utils/debug';
import { DEFAULT_WEEKDAYS } from 'utils/summary';
import { ISO_WEEKDAYS } from 'utils/work-days';

const DEFAULT_END_DATE = '2100-01-01';

/**
 * Keeps a persistent cache of DateTime instances for dates.
 *
 * The capacity util functions are called on every frame as you scroll the tasks timeline, and the
 * Luxon calls are expensive.
 */
const cache = new Map<string, DateTime>([[DEFAULT_END_DATE, DateTime.fromISO(DEFAULT_END_DATE)]]);

/**
 * Get a cached DateTime instance for a given date string.
 *
 * This util makes the assumption that the time of day is irrelevant, and will always return the
 * start of the parsed day.
 */
export const cachedFromISO = (text: string): DateTime => {
  let value = cache.get(text);
  if (!value) {
    value = DateTime.fromISO(text).startOf('day');
    cache.set(text, value);
  }
  return value;
};

export function getHistoricCapacityByDate(
  userCapacityHistory: Array<RawCapacity>,
  day: string
): RawCapacity | null {
  if (userCapacityHistory == null) {
    return null;
  }
  debug.assert(Array.isArray(userCapacityHistory), 'expected userCapacityHistory to be an array');
  return getHistoricCapacityByDateDateTime(
    parseCapacityHistory(userCapacityHistory),
    cachedFromISO(day)
  );
}

export interface ParsedCapacity {
  start_date: DateTime;
  current: boolean;
  end_date: DateTime | null;
  raw: RawCapacity;
}

// Allows parsing capacity to DateTime objects once instead of doing it every time
function getHistoricCapacityByDateDateTime(
  userCapacityHistory: ReadonlyArray<ParsedCapacity>,
  day: DateTime
): RawCapacity | null {
  if (userCapacityHistory == null) {
    return null;
  }
  return (
    userCapacityHistory.reduce((currentPickedCapacity: RawCapacity | null, capacity) => {
      if (capacity == null) {
        return null;
      }
      if (currentPickedCapacity != null) {
        return currentPickedCapacity;
      }

      if (day >= capacity.start_date && capacity.current) {
        return capacity.raw;
      }

      if (day >= capacity.start_date && capacity.end_date != null && day <= capacity.end_date) {
        return capacity.raw;
      }
      return currentPickedCapacity;
    }, null) ?? null
  );
}
getHistoricCapacityByDate.dateTime = getHistoricCapacityByDateDateTime;

export function getDailyBaseCapacityByDate(
  userCapacityHistory: Array<RawCapacity>,
  date: string
): number | null {
  if (userCapacityHistory == null) {
    return null;
  }
  return getDailyBasecapacityByDateDateTime(parseCapacityHistory(userCapacityHistory), date);
}
export function getDailyBasecapacityByDateDateTime(
  userCapacityHistory: ReadonlyArray<ParsedCapacity>,
  date: string
): number | null {
  const parsedDate = cachedFromISO(date);
  const capacity = getHistoricCapacityByDateDateTime(userCapacityHistory, parsedDate);
  if (!capacity) {
    return null;
  }

  const capacityWeekdays = (capacity.work_days || DEFAULT_WEEKDAYS).toUpperCase().split(',');
  const weekDay = ISO_WEEKDAYS[parsedDate.weekday - 1];
  if (!capacityWeekdays.includes(weekDay)) {
    return null;
  }
  return capacity.daily_capacity;
}
getDailyBaseCapacityByDate.dateTime = getDailyBasecapacityByDateDateTime;

export interface Capacity extends Omit<RawCapacity, 'start_date' | 'end_date' | 'work_days'> {
  start_date: DateTime;
  end_date: DateTime;
  work_days: Array<string>;
  work_days_string: string;
}

/**
 * Calculates the total capacity for a user within a given time period.
 *
 * If `from` or `to` are missing, it will return 0 - they are only optional to relax the type
 * requirements and avoid casting.
 *
 * @returns Total capacity in hours. Minimum 0.
 */
export const calculateTotalCapacityForPeriod = (
  from?: string | null,
  to?: string | null,
  userCapacities: Array<RawCapacity> = []
): number => {
  if (!from || !to || !userCapacities.length) {
    return 0;
  }

  const capacities = convertRawCapacityToCapacity(userCapacities);

  const daysInRange = getDaysInRange(from, to);
  if (daysInRange.length === 0) {
    return 0;
  }

  const oldestCapacity = capacities[0];
  const sum = daysInRange.reduce((temp, day) => {
    const weekday = day.toFormat('ccc').toUpperCase();
    if (day < oldestCapacity.start_date && oldestCapacity.work_days.includes(weekday)) {
      return temp + oldestCapacity.daily_capacity;
    }

    const capacity = capacities.find(c => {
      return (
        Interval.fromDateTimes(c.start_date, c.end_date).contains(day) &&
        c.work_days.includes(weekday)
      );
    });

    return temp + (capacity?.daily_capacity ?? 0);
  }, 0);

  return sum;
};

export function parseCapacityHistory(
  userCapacityHistory: Array<RawCapacity>
): ReadonlyArray<ParsedCapacity> {
  return userCapacityHistory.map(rawCapacity => ({
    raw: rawCapacity,
    current: rawCapacity.current,
    start_date: cachedFromISO(rawCapacity.start_date),
    end_date: rawCapacity.end_date == null ? null : cachedFromISO(rawCapacity.end_date),
  }));
}

const getDaysInRange = (from: string, to: string) => {
  const dtFrom = cachedFromISO(from);
  const dtTo = cachedFromISO(to);

  const numDaysInRange = dtTo.diff(dtFrom, 'days').days;
  if (numDaysInRange < 0) {
    return [];
  }

  const daysInRange: Array<DateTime> = [];
  for (let i = 0; i <= numDaysInRange; ++i) {
    daysInRange.push(cachedFromISO(from).plus({ days: i }));
  }

  return daysInRange;
};

export const getNumberOfWorkingDaysForAUser = (
  from?: string | null,
  to?: string | null,
  userCapacities: Array<RawCapacity> = []
): number => {
  if (!from || !to || !userCapacities.length) {
    return -1;
  }
  let numWorkingDays = 0;
  const capacities = getRelevantCapacitiesForAUser(from, to, userCapacities);
  const daysInRange = getDaysInRange(from, to);

  daysInRange.forEach(day => {
    const weekday = day.toFormat('ccc').toUpperCase();
    const capacity = capacities.find(c => c.date.equals(day));
    if (capacity?.capacity?.work_days_string.includes(weekday)) {
      numWorkingDays++;
    }
  });

  return numWorkingDays;
};

const convertRawCapacityToCapacity = (userCapacities: Array<RawCapacity>) => {
  return userCapacities
    .map<Capacity>(capacity => {
      const workDays = capacity.work_days || DEFAULT_WEEKDAYS;
      return {
        ...capacity,
        start_date: cachedFromISO(capacity.start_date),
        end_date: capacity.end_date
          ? cachedFromISO(capacity.end_date)
          : cache.get(DEFAULT_END_DATE)!,
        work_days_string: workDays,
        work_days: workDays.split(','),
      };
    })
    .sort((a, b) => (a.start_date < b.start_date ? -1 : 1));
};

export const getRelevantCapacitiesForAUser = (
  from?: string | null,
  to?: string | null,
  userCapacities: Array<RawCapacity> = []
) => {
  if (!from || !to || !userCapacities.length) {
    return [];
  }

  const daysInRange = getDaysInRange(from, to);
  if (daysInRange.length === 0) {
    return [];
  }

  const capacities = convertRawCapacityToCapacity(userCapacities);

  const oldestCapacity = capacities[0];
  return daysInRange.map(day => {
    const weekday = day.toFormat('ccc').toUpperCase();
    if (day < oldestCapacity.start_date && oldestCapacity.work_days.includes(weekday)) {
      return { date: day, capacity: oldestCapacity };
    }

    const capacity = capacities.find(c => {
      return (
        Interval.fromDateTimes(c.start_date, c.end_date).contains(day) &&
        c.work_days.includes(weekday)
      );
    });

    return { date: day, capacity: capacity };
  });
};
